import React from "react";
import { useFetch } from "lib/useFetch";
import ErrorBoundary from "lib/ErrorBoundary";
import { ListGroup, ListGroupItem } from "react-bootstrap";
import { LoadingSpinner } from "lib/Spinner";
import { DataErrorMsg } from "pages/analystDirectory/AnalystDirectory";
import { ShareButton } from "pages/latestResearch/shareMenu/ShareButton";
import { PublicationDate, Analysts, Tags, Title } from "pages/latestResearch/publications/listView/PublicationItems";
import { i18n, _t, isJa, isEn } from "app/i18n/i18n";
import { formatResponse } from "pages/latestResearch/publications/JAPIPublicationLoader";
import { NoInternetMsg } from "pages/login/LogIn";
import { RemoveButton } from "./parts/RemoveButton";
import { ResearchBreadcrumb } from "pages/latestResearch/publications/breadcrumb/Breadcrumb";
import { EmptyReadingList } from "./parts/EmptyReadingList";
import { useSet } from "../../lib/hooks/useSet";
import { FaIcon } from "lib/FaIcon";

export const READING_LIST_URL = "/research/japi/user/reading-list";

export const ReadingList = () => {
  const [data, isLoading, isError] = useFetch(READING_LIST_URL, null, "GET");
  const [pubs, isParseError] = parsePubs(data);
  const readingList = useSet("readingList", pubs, [data]);
  const lang = i18n.getLanguage();

  if (!navigator.onLine) return <NoInternetMsg />;
  if (isLoading) return <LoadingSpinner />;
  if (isError || isParseError) return <DataErrorMsg />;
  if (!readingList.length) return <EmptyReadingList />;

  return (
    <ErrorBoundary>
      <ReadingListHeading readingList={readingList} />

      <ListGroup className="mb-5">
        {readingList.map((publication) => (
          <ReadingListItem key={publication.id} publication={publication} lang={lang} />
        ))}
      </ListGroup>
    </ErrorBoundary>
  );
};

const ReadingListHeading = ({ readingList }) => (
  <header>
    <ResearchBreadcrumb />
    <p className="text-center">
      <FaIcon icon="fab fa-stack-overflow fa-2x mt-3" align="middle" />
      <br />
      {isEn() && _t("You have " + readingList.length + " publication(s) in your Reading List: ")}
      {isJa() && `リーディングリストに${readingList.length}個のコンテンツがあります。`}
    </p>
  </header>
);

const ReadingListItem = ({ publication, lang }) => (
  <ListGroupItem className="d-flex p-1">
    <RemoveButton {...publication} />
    <article className="w-100 border-0">
      <ShareButton publication={publication} />
      <Title {...publication} />
      <PublicationDate {...publication} />
      <Analysts lang={lang} {...publication} />
      <Tags lang={lang} {...publication} />
    </article>
  </ListGroupItem>
);

// reading list UI now expects JSON from server
function parsePubs(data) {
  try {
    let pubs = [],
      isError = false;
    const hits = data.searchResponse && data.searchResponse.hits.hits;
    const source = hits && hits.map((hit) => hit.sourceAsMap);
    if (source) pubs = formatResponse(source);
    return [pubs, isError];
  } catch (err) {
    console.error("Error parsing reading list", err);
    return [[], true];
  }
}
